import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverBody,
  PopoverHeader,
  Row,
} from "reactstrap";
import { getPriceReductionAiRecommendationApi } from "../../../services/manage-product/productService";
import CalculationPopover from "./CalculationPopover";
import DiscountAnalysisDrawer from "./DiscountAnalysisDrawer";
import ProductOverview from "./ProductOverview";

const formatNumber = (value, maximumFractionDigits = 4) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number)
    ? value
    : number.toLocaleString(undefined, { maximumFractionDigits });
};

const formatCurrency = (value) => {
  const formatted = formatNumber(value, 2);
  return formatted === "-" ? formatted : `£${formatted}`;
};

const formatPercentage = (value) => {
  const formatted = formatNumber(value, 2);
  return formatted === "-" ? formatted : `${formatted}%`;
};

const formatRatioPercentage = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return formatPercentage(Number(value) * 100);
};

const decisionDetailFields = [
  ["grade_rule", "Grade Rule"],
  ["lifecycle_rule", "Lifecycle Rule"],
  ["clearance_requirement", "Clearance Requirement"],
  ["why_not_lower_discount", "Why Not a Lower Discount"],
  ["why_not_higher_discount", "Why Not a Higher Discount"],
  ["selected_discount_result", "Selected Discount Result"],
];

const DetailItem = ({ label, value }) => (
  <Col md={4} className="mb-2">
    <div className="border rounded p-3 h-100">
      <div className="text-muted small mb-1">{label}</div>
      <div className="font-weight-bold" style={{ wordBreak: "break-word" }}>{value}</div>
    </div>
  </Col>
);

const ProfitVsBestProfitItem = ({ result, index }) => {
  const targetId = `profit-vs-best-trigger-${index}`;
  const ratioValue = result.profit_vs_best_profit_pct;
  const ratio = ratioValue === null || ratioValue === undefined || ratioValue === ""
    ? NaN
    : Number(ratioValue);
  const selectedProfit = result.selected_discount_profit ?? result.expected_profit;
  const numericSelectedProfit = Number(selectedProfit);
  const suppliedBestProfit = result.best_profit_from_all_simulated_discounts ?? result.best_profit;
  const bestProfit = suppliedBestProfit ?? (
    Number.isFinite(ratio) && ratio !== 0 && Number.isFinite(numericSelectedProfit)
      ? numericSelectedProfit / ratio
      : null
  );

  return (
    <Col md={4} className="mb-2">
      <div className="border rounded p-3 h-100">
        <div className="text-muted small mb-1">
          Profit vs Best Profit
          <CalculationPopover
            targetId={targetId}
            title="Profit vs Best Profit Calculation"
            formula="Selected Discount Profit ÷ Best Profit from All Simulated Discounts"
            calculation={(
              <>
                {formatCurrency(selectedProfit)} ÷ {formatCurrency(bestProfit)} = {formatNumber(ratioValue)}
              </>
            )}
            description={(
              <>
                The selected discount keeps <strong>{formatRatioPercentage(ratioValue)}</strong> of the best possible profit.
              </>
            )}
          />
        </div>
        <div className="font-weight-bold" style={{ wordBreak: "break-word" }}>
          {formatRatioPercentage(result.profit_vs_best_profit_pct)}
        </div>
      </div>

    </Col>
  );
};

const PaceRatioItem = ({ result, index }) => {
  const hasClearanceWindow = result.clearance_window_days !== null
    && result.clearance_window_days !== undefined
    && result.clearance_window_days !== "";
  const clearanceWindowDays = hasClearanceWindow ? Number(result.clearance_window_days) : NaN;
  const monthsRemaining = Number.isFinite(clearanceWindowDays)
    ? clearanceWindowDays / 30
    : null;
  const pacePercentage = result.pace_ratio === null
    || result.pace_ratio === undefined
    || result.pace_ratio === ""
    ? null
    : Number(result.pace_ratio) * 100;

  return (
    <Col md={4} className="mb-2">
      <div className="border rounded p-3 h-100">
        <div className="text-muted small mb-1">
          Pace Ratio
          <CalculationPopover
            targetId={`pace-ratio-trigger-${index}`}
            title="Pace Ratio Calculation"
            formula="Predicted Sales ÷ Required 30-day Pace Sales"
            calculation={(
              <div>
                <div className="mb-1">
                  Months remaining: {formatNumber(result.clearance_window_days)} ÷ 30 = {formatNumber(monthsRemaining)}
                </div>
                <div className="mb-1">
                  Required 30-day sales: {formatNumber(result.total_stock)} ÷ {formatNumber(monthsRemaining)} = {formatNumber(result.required_30d_sales)}
                </div>
                <div>
                  Pace ratio: {formatNumber(result.predicted_sales)} ÷ {formatNumber(result.required_30d_sales)} = {formatNumber(result.pace_ratio)}
                </div>
              </div>
            )}
            description={pacePercentage === null || Number.isNaN(pacePercentage) ? null : (
              <>
                This achieves <strong>{formatPercentage(pacePercentage)}</strong> of the required 30-day clearance pace.
              </>
            )}
          />
        </div>
        <div className="font-weight-bold" style={{ wordBreak: "break-word" }}>
          {formatNumber(result.pace_ratio)}
        </div>
      </div>
    </Col>
  );
};

const DetailSection = ({ title, label, status, children }) => (
  <div className="mb-4">
    <div className="d-flex align-items-center justify-content-between mb-2">
      <div className="d-flex align-items-center">
        <h6 className="font-weight-bold mb-0">{title}</h6>
        {label}
      </div>
      <div>{status}</div>
    </div>
    <Row>{children}</Row>
  </div>
);

const SalesSummaryItem = ({ label, value, columnSize = 3 }) => (
  <Col sm={columnSize} className="sales-info-cell">
    <div className="px-3 py-3 h-100">
      <div className="sales-info-label text-muted">{label}</div>
      <div className="sales-info-value font-weight-bold">{value}</div>
    </div>
  </Col>
);

const expenseBreakdownFields = [
  ["cost_of_goods_sold", "Cost of Goods Sold"],
  ["commissions", "Commissions"],
  ["additional_charges", "Additional Charges"],
  ["other_additional_charges", "Other Additional Charges"],
  ["agent_commissions", "Agent Commissions"],
  ["gateway_fees", "Gateway Fees"],
  ["shipping_cost", "Shipping Cost"],
  ["return_shipping_cost", "Return Shipping Cost"],
];

const BreakdownRow = ({ label, value, deduction = false, strong = false, divider = false }) => {
  const formattedValue = formatCurrency(value);
  const displayValue = deduction && formattedValue !== "-"
    ? `-${formattedValue}`
    : formattedValue;

  return (
    <div
      className={`d-flex justify-content-between py-1 ${strong ? "font-weight-bold" : ""}`}
      style={divider ? { borderTop: "2px solid #dee2e6", marginTop: "4px", paddingTop: "8px" } : undefined}
    >
      <span className={strong ? "mr-3" : "text-muted mr-3"}>{label}</span>
      <span>{displayValue}</span>
    </div>
  );
};

const SalesDetails = ({ details, compact = false }) => {
  const [profitPopoverOpen, setProfitPopoverOpen] = useState(false);

  if (!details) return null;

  const financialBreakdown = details.financial_breakdown || {};

  return (
    <div
      className={compact ? "mt-2" : "border rounded p-3 mb-4"}
      style={compact ? undefined : { backgroundColor: "#f8f9fa" }}
    >
      <div className="mb-2">
        <span
          className="badge badge-pill px-3 py-2"
          style={{ backgroundColor: "#e9f7ef", color: "#247a47", fontSize: "12px" }}
        >
          Sales Details
        </span>
      </div>
      <div className="sales-info-grid rounded overflow-hidden" style={{ backgroundColor: "#fff" }}>
        <Row className="sales-info-row m-0">
          <SalesSummaryItem label="Sold Quantity" value={formatNumber(details.total_sold_quantity)} columnSize={4} />
          <SalesSummaryItem label="Returned Quantity" value={formatNumber(details.returned_quantity)} columnSize={4} />
          <SalesSummaryItem label="Net Sold Quantity" value={formatNumber(details.net_sold_quantity)} columnSize={4} />
        </Row>
        <Row className="sales-info-row m-0">
        <SalesSummaryItem
          label="Sell-through Rate"
          value={formatPercentage(details.current_sell_through_rate)}
        />
        <SalesSummaryItem
          label="Total Income"
          value={formatCurrency(financialBreakdown.total_income)}
        />
        <Col sm={3} className="sales-info-cell">
          <div
            id="ai-profit-details-trigger"
            className="px-3 py-3 h-100"
            role="button"
            tabIndex={0}
            onClick={() => setProfitPopoverOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setProfitPopoverOpen((open) => !open);
              }
            }}
            style={{ cursor: "pointer" }}
            title="Click to view the financial breakdown"
          >
            <div className="sales-info-label text-muted">
              Profit So Far <i className="fa fa-info-circle ml-1" />
            </div>
            <div className="sales-info-value font-weight-bold">{formatCurrency(details.profit_so_far)}</div>
          </div>
          <Popover
            placement="auto"
            target="ai-profit-details-trigger"
            isOpen={profitPopoverOpen}
            toggle={() => setProfitPopoverOpen((open) => !open)}
            style={{ maxWidth: "420px", width: "420px" }}
          >
            <PopoverHeader>
              <div className="d-flex align-items-center justify-content-between" style={{ width: "100%" }}>
                <span>Financial Breakdown</span>
                <button
                  type="button"
                  className="close ml-3"
                  aria-label="Close financial breakdown"
                  onClick={() => setProfitPopoverOpen(false)}
                  style={{ fontSize: "20px", lineHeight: 1 }}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            </PopoverHeader>
            <PopoverBody style={{ maxHeight: "55vh", overflowY: "auto" }}>
              <div className="mb-3">
                <span className="badge badge-success mb-2">INCOME</span>
                <BreakdownRow label="Gross Sales" value={financialBreakdown.gross_sales} />
                <BreakdownRow label="Discounts" value={financialBreakdown.discounts} deduction />
                <BreakdownRow label="Returns" value={financialBreakdown.returns} deduction />
                <BreakdownRow label="Net Sales" value={financialBreakdown.net_sales} strong divider />
                <BreakdownRow label="Shipping Income" value={financialBreakdown.shipping_income} />
                <BreakdownRow label="Return Shipping Income" value={financialBreakdown.return_shipping_income} />
                <BreakdownRow label="Total Income" value={financialBreakdown.total_income} strong divider />
              </div>

              <div className="mb-3">
                <span className="badge badge-danger mb-2">EXPENSE</span>
                {expenseBreakdownFields.map(([key, label]) => (
                  <BreakdownRow key={key} label={label} value={financialBreakdown[key]} />
                ))}
                <BreakdownRow label="Total Expense" value={financialBreakdown.total_expense} strong divider />
              </div>

              <div style={{ borderTop: "3px double #adb5bd", paddingTop: "10px" }}>
                <div className="d-flex justify-content-between font-weight-bold mb-1">
                  <span className="mr-3">Gross Profit</span>
                  <span className="text-success">{formatCurrency(financialBreakdown.gross_profit)}</span>
                </div>
                <div className="d-flex justify-content-between font-weight-bold">
                  <span className="mr-3">Profit Margin</span>
                  <span className="text-success">
                    {formatPercentage(financialBreakdown.profit_margin_percentage)}
                  </span>
                </div>
              </div>
            </PopoverBody>
          </Popover>
        </Col>
        <Col sm={3} className="sales-info-cell">
          <div className="px-3 py-3 h-100">
            <div className="sales-info-label text-muted">
              Profit Margin
              <CalculationPopover
                targetId="ai-profit-margin-trigger"
                title="Profit Margin Calculation"
                formula="Gross Profit ÷ Total Income × 100"
                calculation={(
                  <>
                    {formatCurrency(financialBreakdown.gross_profit)} ÷ {formatCurrency(financialBreakdown.total_income)} × 100
                  </>
                )}
              />
            </div>
            <div className="sales-info-value font-weight-bold">
              {formatPercentage(financialBreakdown.profit_margin_percentage)}
            </div>
          </div>
        </Col>
        </Row>
      </div>
    </div>
  );
};

const shouldShowDiscountAnalysis = (result) => {
  const isOffTrack = result.on_track === false
    || result.on_track === 0
    || result.on_track === "0"
    || result.on_track === "false";
  return isOffTrack || Number(result.remaining_stock) > 0;
};

const RecommendationResult = ({ result, index, productId }) => (
  <div className={index > 0 ? "border-top pt-4 mt-4" : ""}>
    <h5 className="font-weight-bold mb-3">
      {result.product_code || "Product"}{result.colour ? ` - ${result.colour}` : ""}
    </h5>

    <DetailSection title="Pricing and Forecast (Next 30 Days)">
      <DetailItem label="Recommended Discount" value={formatPercentage(result.recommended_discount)} />
      <DetailItem label="Current Discount" value={formatPercentage(result.current_discount)} />
      <DetailItem label="Simulated Sale Price" value={formatCurrency(result.simulated_sale_price)} />
      <DetailItem label="Predicted Sales" value={formatNumber(result.predicted_sales)} />
      <DetailItem label="Remaining Stock" value={formatNumber(result.remaining_stock)} />
      <DetailItem label="Expected Revenue" value={formatCurrency(result.expected_revenue)} />
      <DetailItem label="Expected Profit" value={formatCurrency(result.expected_profit)} />
      <DetailItem label="Profit Margin" value={formatRatioPercentage(result.profit_margin_pct)} />
      <ProfitVsBestProfitItem result={result} index={index} />
    </DetailSection>

    <DetailSection
      title="Lifecycle and Clearance"
      label={result.lifecycle_status && (
        <span
          className="badge badge-pill ml-2"
          style={{ backgroundColor: "#e8f1ff", color: "#2457a7", fontWeight: 600 }}
        >
          Lifecycle: {result.lifecycle_status}
        </span>
      )}
      status={(
        <div className="d-flex align-items-center">
          {result.on_track === null || result.on_track === undefined ? (
            <span className="badge badge-pill badge-secondary px-3 py-2">Track Status Unknown</span>
          ) : result.on_track ? (
            <span className="badge badge-pill badge-success px-3 py-2">
              <i className="fa fa-check-circle mr-1" />On Track
            </span>
          ) : (
            <span className="badge badge-pill badge-danger px-3 py-2">
              <i className="fa fa-exclamation-circle mr-1" />Off Track
            </span>
          )}
          {shouldShowDiscountAnalysis(result) && (
            <DiscountAnalysisDrawer
              productId={productId}
              fallbackProductCode={result.product_code}
            />
          )}
        </div>
      )}
    >
      <DetailItem label="30-day Sell-through Rate" value={formatRatioPercentage(result.sell_through_rate_30d)} />
      <DetailItem label="Lifecycle Target Days" value={formatNumber(result.lifecycle_target_days)} />
      <DetailItem label="Product Age Days" value={formatNumber(result.product_age_days)} />
      <DetailItem label="Lifecycle Remaining Days" value={formatNumber(result.lifecycle_remaining_days)} />
      <DetailItem label="Clearance Window Days" value={formatNumber(result.clearance_window_days)} />
      <DetailItem label="Required 30-day Sales" value={formatNumber(result.required_30d_sales)} />
      <DetailItem label="Estimated Months to Clear" value={formatNumber(result.estimated_months_to_clear)} />
      <DetailItem label="Estimated Days to Clear" value={formatNumber(result.estimated_days_to_clear)} />
      <PaceRatioItem result={result} index={index} />
    </DetailSection>

    <div className="border rounded p-3 mb-3">
      <h6 className="font-weight-bold mb-2">Reason</h6>
      <div>{result.reason || "-"}</div>
    </div>

    <div className="border rounded p-3">
      <h6 className="font-weight-bold mb-3">Decision Details</h6>
      {decisionDetailFields.map(([key, label]) => (
        <div key={key} className="mb-3">
          <div className="font-weight-bold mb-1">{label}</div>
          <div className="text-muted">{result.decision_details?.[key] || "-"}</div>
        </div>
      ))}
    </div>
  </div>
);

const AiRecommendationDetailsModal = ({ product, onClose }) => {
  const productId = product?.id;
  const [results, setResults] = useState([]);
  const [recommendationProduct, setRecommendationProduct] = useState(null);
  const [salesDetails, setSalesDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const styleCode = recommendationProduct?.style_code
    || recommendationProduct?.style
    || product?.style_code
    || product?.style;

  useEffect(() => {
    if (!productId) {
      setResults([]);
      setRecommendationProduct(null);
      setSalesDetails(null);
      setError("");
      return undefined;
    }

    let isActive = true;
    setLoading(true);
    setError("");
    setResults([]);
    setRecommendationProduct(null);
    setSalesDetails(null);

    getPriceReductionAiRecommendationApi({ product_id: productId })
      .then(({ response }) => {
        if (!isActive) return;
        const responseResults = response?.results || response?.data?.results || [];
        const responseProduct = response?.product || response?.data?.product || null;
        const responseSalesDetails = response?.sales_details || response?.data?.sales_details || null;
        setResults(Array.isArray(responseResults) ? responseResults : []);
        setRecommendationProduct(responseProduct);
        setSalesDetails(responseSalesDetails);
      })
      .catch((requestError) => {
        if (!isActive) return;
        setError(
          requestError.response?.data?.message ||
          requestError.message ||
          "Failed to load AI recommendation details."
        );
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [productId]);

  return (
    <Modal isOpen={!!product} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>
        AI Recommendation Details
        {product?.title ? ` - ${product.title}` : ""}
        {styleCode ? ` (Style: ${styleCode})` : ""}
      </ModalHeader>
      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {loading && (
          <div className="text-center text-muted py-5">
            <i className="fa fa-spinner fa-spin mr-2" />
            Loading AI recommendation details...
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger mb-0">{error}</div>
        )}

        {!loading && !error && results.length === 0 && (
          <div className="text-center text-muted py-4">No AI recommendation details available.</div>
        )}

        {!loading && !error && (
          <ProductOverview
            productId={productId}
            product={recommendationProduct}
            result={results[0]}
            salesDetails={salesDetails}
            salesDetailsContent={<SalesDetails details={salesDetails} compact />}
          />
        )}

        {!loading && !error && results.map((result, index) => (
          <RecommendationResult
            key={result.key || `${result.product_code || "product"}-${index}`}
            result={result}
            index={index}
            productId={productId}
          />
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default AiRecommendationDetailsModal;
